import { useAuctionStore } from "@/stores/AuctionStore";
import type {IActiveLot, IAuction, IHistoryLog, ILiveSettings, ILot, ILotPopupData, IUserListItem} from "@/models";
import { LotStatuses, LotSoldStatuses, BiddingSteps } from "@/enum";
import { ILeaderListItem } from "@/models/api/ILeaderListItem";
import { Emitter } from "mitt";
import { IMessageData } from "@/models";
import { ToastServiceMethods } from "primevue/toastservice";
import { updateLotFieldById } from "@/utils/lot";

export async function initLiveAuctionListener() {
  const auctionStore = useAuctionStore();

  if (auctionStore.auction) {
    window.Echo.join(`live.${auctionStore.auction.id}`)
      .here((users: IUserListItem[]) => {
        auctionStore.$patch((state) => {
          state.userOnLive = users.filter((user) => user.id !== -1);
        });
      })
      .joining((user: IUserListItem) => {
        if (user.id !== -1) {
          auctionStore.userOnLive.push(user);
        }
      })
      .leaving((user: IUserListItem) => {
        auctionStore.userOnLive = auctionStore.userOnLive.filter((item) => item.id !== user.id);
      });

    window.Echo.channel(`live.${auctionStore.auction.id}`)
      .listen(".auction.updated", (e: { auction: IAuction }) => {
        auctionStore.$patch((state) => {
          state.auction = <IAuction>e.auction;
        });
      })
      .listen(".auction.reset_time_line", (e: { auctionId: number }) => {
        if (auctionStore.auction.id === e.auctionId) {
          auctionStore.resetTimeLine();
        }
      })
      .listen(".auction.refresh_step_button", (e: { auctionId: number }) => {
        if (auctionStore.auction.id === e.auctionId) {
          auctionStore.refreshStepButton();
        }
      })
      .listen(".moved_away", (e: { userId: number | string }) => {
        if (typeof e.userId !== "string") {
          auctionStore.$patch((state) => {
            const userIndex = state.userOnLive.findIndex((user: IUserListItem) => user.id === e.userId);
            if (userIndex !== -1) {
              state.userOnLive[userIndex].status = "clarified";
            }
          });
        }
      })
      .listen(".return", (e: { userId: number | string }) => {
        if (typeof e.userId !== "string") {
          auctionStore.$patch((state) => {
            const userIndex = state.userOnLive.findIndex((user: IUserListItem) => user.id === e.userId);
            if (userIndex !== -1) {
              state.userOnLive[userIndex].status = "active";
            }
          });
        }
      })
      .listen(".update_anonymous_count", (e: { total_count: number; no_auth_count: number }) => {
        auctionStore.$patch((state) => {
          state.userOnLiveAnonymous = auctionStore.user?.is_admin ? e.no_auth_count : e.total_count;
        });
      })
      .listen(".live_logs_encrypted.created", (e: { lotId: number; log: IHistoryLog }) => {
        if (auctionStore.activeLot?.id === e.lotId) {
          const logExists = auctionStore.historyLogs.some((log) => log.id === e.log.id);
          if (!logExists) {
            auctionStore.addHistoryLog(e.log, true);
          }
        }
      });

    window.Echo.channel(`live.${auctionStore.auction.id}.lots.bidding_step`).listen(
      ".lots.bidding_step.updated",
      (e: { step: number }) => {
        auctionStore.setCurrentStep(e.step);
      }
    );

    auctionStore.$patch((state) => {
      state.liveAuctionListener = `live.${auctionStore.auction.id}`;
      state.liveAuctionBiddingStepListener = `lots.${auctionStore.auction.id}.bidding_step`;
    });
  }
}

export async function initAuctionBiddersCount() {
  const auctionStore = useAuctionStore();
  const auctionId = auctionStore.auction.id;

  window.Echo.channel(`auctions.${auctionId}.bidders_count`).listen(
    ".auctions.bidders_count.updated",
    (e: { biddersCount: number }) => {
      auctionStore.$patch((state) => {
        state.auction.bidders_count = e.biddersCount;
      });
    }
  );
}

export async function initUserBidsListener() {
  const auctionStore = useAuctionStore();

  if (auctionStore.user) {
    window.Echo.channel("lots.user_bids").listen(
      ".lots.user_bids.updated",
      (e: { lotId: number; userBids: Record<number, number> }) => {
        auctionStore.$patch((storeState) => {
          if (storeState.user?.id && storeState.user.id in e.userBids) {
            storeState.user.bids[e.lotId] = e.userBids[storeState.user.id];
            if (storeState.popupLot?.id === e.lotId) {
              storeState.popupLot.user_bid = e.userBids[storeState.user.id];
            }
          }
        });
      }
    );

    auctionStore.$patch((state) => {
      state.userBidsListener = `lots.user_bids`;
    });
  }
}

export async function initCatalogLotsListener() {
  const auctionStore = useAuctionStore();

  if (auctionStore.auction) {
    window.Echo.channel(`auctions.${auctionStore.auction.id}.live_catalog`).listen(
      ".auctions.live_catalog.updated",
      (e: { auctionId: number; lot: ILot }) => {
        if (auctionStore.auction.id === e.auctionId) {
          auctionStore.$patch((storeState) => {
            const lotIndex = storeState.lots.findIndex((lot) => lot.id === e.lot.id);
            if (lotIndex !== -1) {
              Object.assign(storeState.lots[lotIndex], e.lot);
            }

            const searchLotIndex = storeState.catalog.searchLots?.findIndex((lot) => lot.id === e.lot.id);
            if (searchLotIndex !== undefined && searchLotIndex >= 0 && storeState.catalog.searchLots) {
              Object.assign(storeState.catalog.searchLots[searchLotIndex], e.lot);
            }

            if (storeState.popupLot && storeState.popupLot?.id === e.lot.id) {
              Object.assign(storeState.popupLot, e.lot);
            }
          });
        }
      }
    );

    window.Echo.channel("lots.bid_leader").listen(
      ".lots.bid_leader.changed",
      (e: { leaderId: number; lotId: number }) => {
        updateLotFieldById(e.lotId, "leader_id", e.leaderId);
      }
    );

    window.Echo.channel("lots.bids_count").listen(
      ".lots.bids_count.updated",
      (e: { newCount: number; lotId: number }) => {
        updateLotFieldById(e.lotId, "bids_count", e.newCount);
      }
    );

    auctionStore.$patch((state) => {
      state.catalogLotsListener = `auctions.${auctionStore.auction.id}.live_catalog`;
    });
  }
}

export async function initActiveLotListener() {
  const auctionStore = useAuctionStore();

  if (auctionStore.auction) {
    window.Echo.channel(`auctions.${auctionStore.auction.id}.live_active_lot`).listen(
      ".auctions.live_active_lot.changed",
      (e: { activeLot: IActiveLot, clearLiveLogs: boolean }) => {
        if (e.clearLiveLogs) {
          auctionStore.resetHistoryLog();
        }

        if (auctionStore.activeLot?.id === e.activeLot.id) {
          auctionStore.$patch((state) => {
            if (state.activeLot?.id) {
              state.activeLot.status = <LotStatuses>e.activeLot.status;
              state.activeLot.sold_status = <LotSoldStatuses>e.activeLot.sold_status;
              state.activeLot.live_step = <BiddingSteps>e.activeLot.live_step;
            }
          });
        } else {
          auctionStore.setActiveLot(e.activeLot);
          auctionStore.$patch((state) => {
            const lotIndex = state.lots.findIndex((lot) => lot.id === e.activeLot.id);
            if (lotIndex !== -1) {
              state.lots[lotIndex].status = <LotStatuses>e.activeLot.status;
            }
          });
        }
      }
    );

    auctionStore.$patch((state) => {
      state.activeLotListener = `auctions.${auctionStore.auction.id}.live_active_lot`;
    });
  }
}

export function initCurrentBidListener(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${activeLot.id}.current_bid`).listen(
    ".lots.current_bid.updated",
    (e: { newBid: number }) => {
      auctionStore.$patch((storeState) => {
        if (storeState.activeLot?.id === activeLot.id) {
          storeState.activeLot.current_bid = e.newBid;
        }
      });
    }
  );

  auctionStore.$patch((storeState) => {
    storeState.currentBidListener = `lots.${activeLot.id}.current_bid`;
  });
}

export function initLeaderChangedListener(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${activeLot.id}.bid_leader`).listen(
    ".lots.bid_leader.changed",
    (e: { leaderId: number }) => {
      auctionStore.$patch((state) => {
        if (state.activeLot?.id && state.activeLot.id === activeLot.id) {
          state.activeLot.leader_id = e.leaderId;
        }
      });
    }
  );

  auctionStore.$patch((state) => {
    state.currentBidListener = `lots.${activeLot.id}.bid_leader`;
  });
}

export function initLeadersListUpdatedListener(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  if (auctionStore.user?.is_admin) {
    window.Echo.private(`lots.${activeLot.id}.leaders_list`).listen(
      ".lots.leaders_list.updated",
      (e: { lotId: number; leadersList: ILeaderListItem[] }) => {
        auctionStore.$patch((state) => {
          if (state.activeLot?.id && state.activeLot.id === activeLot.id) {
            state.activeLot.leaders_list = e.leadersList;
          }
        });
      }
    );

    auctionStore.$patch((state) => {
      state.leadersListUpdatedListener = `lots.${activeLot.id}.leaders_list`;
    });
  }
}

export function initBidsCountListener(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${activeLot.id}.bids_count`).listen(
    ".lots.bids_count.updated",
    (e: { newCount: number }) => {
      auctionStore.$patch((state) => {
        if (state.activeLot?.id && state.activeLot.id === activeLot.id) {
          state.activeLot.bids_count = e.newCount;
        }
      });
    }
  );

  auctionStore.$patch((state) => {
    state.bidsCountListener = `lots.${activeLot.id}.bids_count`;
  });
}

export function initBookBidListener(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${activeLot.id}.book_bid`).listen(
    ".lots.book_bid.updated",
    (e: { newBid: number }) => {
      auctionStore.$patch((state) => {
        if (state.activeLot?.id && state.activeLot.id === activeLot.id) {
          state.activeLot.book_bid = e.newBid;
        }
      });
    }
  );

  auctionStore.$patch((state) => {
    state.bidsCountListener = `lots.${activeLot.id}.bids_count`;
  });
}

export function initPopupUserBidListener(popupLot: ILotPopupData) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${popupLot.id}.current_bid`).listen(
      ".lots.current_bid.updated",
      (e: { newBid: number }) => {
        auctionStore.$patch((storeState) => {
          if (storeState.popupLot?.id === popupLot.id) {
            storeState.popupLot.current_bid = e.newBid;
          }
        });
      }
  );

  auctionStore.$patch((storeState) => {
    storeState.popupCurrentBidListener = `lots.${popupLot.id}.current_bid`;
  });
}

export function initPopupCurrentBidListener(popupLot: ILotPopupData) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${popupLot.id}.current_bid`).listen(
      ".lots.current_bid.updated",
      (e: { newBid: number }) => {
        auctionStore.$patch((storeState) => {
          if (storeState.popupLot?.id === popupLot.id) {
            storeState.popupLot.current_bid = e.newBid;
          }
        });
      }
  );

  auctionStore.$patch((storeState) => {
    storeState.popupCurrentBidListener = `lots.${popupLot.id}.current_bid`;
  });
}

export function initPopupBidsCountListener(popupLot: ILotPopupData) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`lots.${popupLot.id}.bids_count`).listen(
      ".lots.bids_count.updated",
      (e: { newCount: number }) => {
        auctionStore.$patch((state) => {
          if (state.popupLot?.id && state.popupLot.id === popupLot.id) {
            state.popupLot.bids_count = e.newCount;
          }
        });
      }
  );

  auctionStore.$patch((state) => {
    state.bidsCountListener = `lots.${popupLot.id}.bids_count`;
  });
}

export function initLiveChatListener(emitter: Emitter<Record<string, unknown>>) {
  const auctionStore = useAuctionStore();

  window.Echo.channel(`live_chat.${auctionStore.auction.id}.message`)
    .listen(".live_chat.new", (data: { message: IMessageData }) => {
      emitter.emit("on:ws-chat-new-admin-message", data);
    })
    .listen(".live_chat.pin", (data: { message: IMessageData }) => {
      emitter.emit("on:ws-chat-pin-message", data);
    });

  auctionStore.$patch((state) => {
    state.liveChatListener = `live_chat.${auctionStore.auction.id}.message`;
  });
}

export async function initAuctionSettingsListener() {
  const auctionStore = useAuctionStore();
  const auctionId = auctionStore.auction.id;

  if (!auctionStore.user?.is_admin) {
    window.Echo.channel(`auctions.${auctionId}.settings`).listen(
      ".auctions.settings.changed",
      (e: { settings: ILiveSettings }) => {
        auctionStore.$patch((state) => {
          state.liveSettings = e.settings;
        });
      }
    );

    auctionStore.$patch((state) => {
      state.auctionSettingsListener = `auctions.${auctionId}.settings`;
    });
  }
}

export async function initUserEventsListener(toast: ToastServiceMethods, emitter: Emitter<Record<string, unknown>>) {
  const auctionStore = useAuctionStore();
  const userId = auctionStore.user?.id;

  if (userId) {
    const channel = window.Echo.private(`user.${userId}`);
    channel.listen(".create_bid", (e: { lotId: number; userId: number; amount: number }) => {
      auctionStore.$patch((state) => {
        if (state.user?.id === e.userId && state.user.bids) {
          state.user.bids[e.lotId] = e.amount;
        }
      });
    });
    channel.listen(".delete_bid", (e: { lotId: number; userId: number; amount: number }) => {
      auctionStore.$patch((state) => {
        if (state.user?.id === e.userId && state.user.bids) {
          delete state.user.bids[e.lotId];
        }
      });
    });
    channel.listen(".add_favorite_lot", (e: { lotId: number; userId: number }) => {
      auctionStore.$patch((state) => {
        const index = state.lots.findIndex((lot: ILot) => lot.id === e.lotId);
        if (index != -1) {
          state.lots[index].in_wishlist = true;
          if (state.user) {
            if (!state.user.favorite_lots) {
              state.user.favorite_lots = [e.lotId];
            } else {
              state.user.favorite_lots?.push(e.lotId);
            }
          }
        }
      });
    });
    channel.listen(".delete_favorite_lot", (e: { lotId: number; userId: number }) => {
      auctionStore.$patch((storeState) => {
        const index = storeState.lots.findIndex((lot: ILot) => lot.id === e.lotId);
        if (index != -1) {
          storeState.lots[index].in_wishlist = false;
          if (storeState.user) {
            if (!storeState.user.favorite_lots) {
              storeState.user.favorite_lots = [];
            } else {
              storeState.user.favorite_lots = storeState.user.favorite_lots.filter((i) => i !== e.lotId);
            }
          }
        }
      });
    });
    channel.listen(".update_state", (e: { userId: number; customer_is_muted: boolean; customer_status: number }) => {
      auctionStore.$patch((state) => {
        if (state.user) {
          state.user.customer_is_muted = e.customer_is_muted;
          state.user.customer_status = e.customer_status;
        }
      });
    });
    channel.listen(".live_logs.created", (e: { lotId: number; log: IHistoryLog }) => {
      auctionStore.$patch((state) => {
        if (state.user && auctionStore.activeLot && auctionStore.activeLot.id === e.lotId) {
          const logExists = auctionStore.historyLogs.some((log) => log.id === e.log.id);
          if (!logExists) {
            auctionStore.addHistoryLog(e.log, true);
          }
        }
      });
    });

    channel.listen(
      `.live_chat.${auctionStore.auction.id}.new_message`,
      (data: { userId: number; message: IMessageData }) => {
        emitter.emit("on:ws-chat-new-message", data);
      }
    );

    auctionStore.$patch((state) => {
      state.websocketChannels[`user.${userId}`] = channel;
      state.userEventsListener = `user.${userId}`;
    });
  }
}

export async function initSellerEventsListener(emitter: Emitter<Record<string, unknown>>) {
  const auctionStore = useAuctionStore();
  const auctionId = auctionStore.auction.id;

  if (auctionStore.user?.id && auctionStore.user.is_admin) {
    const channel = window.Echo.private(`live.${auctionId}.admin`);
    channel.listen(
      ".user.create_bid",
      (e: { auctionId: number; userId: number; userData: IUserListItem; lotId: number; isCreate: number }) => {
        auctionStore.$patch((state) => {
          if (state.activeLot?.id == e.lotId) {
            if (!state.activeLot.bidders?.find((user) => user.id == e.userId)) {
              state.activeLot.bidders?.push(e.userData);
            }
          }
        });
      }
    );
    channel.listen(".settings.changed", (e: { settings: ILiveSettings }) => {
      auctionStore.$patch((state) => {
        state.liveSettings = e.settings;
      });
    });
    channel.listen(".live_logs.created", (e: { lotId: number; log: IHistoryLog }) => {
      auctionStore.$patch(() => {
        if (auctionStore.activeLot?.id === e.lotId) {
          const logExists = auctionStore.historyLogs.some((log) => log.id === e.log.id);
          if (!logExists) {
            auctionStore.addHistoryLog(e.log, true);
          }
        }
      });
    });
    channel.listen(
      `.live_chat.${auctionStore.auction.id}.new_message`,
      (data: { userId: number; message: IMessageData }) => {
        emitter.emit("on:ws-chat-new-message", data);
      }
    );

    window.Echo.private("live.admin.common").listen(
      ".update_state",
      (e: { userId: number; customer_is_muted: boolean; customer_status: number }) => {
        auctionStore.$patch((storeState) => {
          if (storeState.userData[e.userId]) {
            storeState.userData[e.userId].customer_status = e.customer_status;
            storeState.userData[e.userId].customer_id_muted = e.customer_is_muted;
          }
        });
      }
    );

    auctionStore.$patch((state) => {
      state.websocketChannels[`live.${auctionId}.admin`] = channel;
      state.sellerEventsListener = `live.${auctionId}.admin`;
    });
  }
}

export function updateAuctionListeners(toast: ToastServiceMethods, emitter: Emitter<Record<string, unknown>>) {
  const auctionStore = useAuctionStore();

  auctionStore.$patch((state) => {
    state.websocketChannels = {};
  });

  // Auction
  if (auctionStore.liveAuctionListener) {
    removeListener(auctionStore.liveAuctionListener);
  }
  if (auctionStore.liveAuctionBiddingStepListener) {
    removeListener(auctionStore.liveAuctionBiddingStepListener);
  }
  initLiveAuctionListener();

  // Active lot
  if (auctionStore.activeLotListener) {
    removeListener(auctionStore.activeLotListener);
  }
  initActiveLotListener();

  // Catalog lots
  if (auctionStore.catalogLotsListener) {
    removeListener(auctionStore.catalogLotsListener);
  }
  initCatalogLotsListener();

  // Catalog lots
  if (auctionStore.auctionSettingsListener) {
    removeListener(auctionStore.auctionSettingsListener);
  }
  initAuctionSettingsListener();

  if (auctionStore.liveChatListener) {
    removeListener(auctionStore.liveChatListener);
  }
  initLiveChatListener(emitter);

  // User
  if (auctionStore.user?.id) {
    if (auctionStore.userBidsListener) {
      removeListener(auctionStore.userBidsListener);
    }
    initUserBidsListener();

    if (auctionStore.sellerEventsListener) {
      removeListener(auctionStore.sellerEventsListener);
    }
    initSellerEventsListener(emitter);

    if (auctionStore.userEventsListener) {
      removeListener(auctionStore.userEventsListener);
    }
    initUserEventsListener(toast, emitter);
  }
}

export function updateActiveLotListeners(activeLot: IActiveLot) {
  const auctionStore = useAuctionStore();

  // Current price
  if (auctionStore.currentBidListener) {
    removeListener(auctionStore.currentBidListener);
  }
  initCurrentBidListener(activeLot);

  // Leader ID
  if (auctionStore.leaderChangedListener) {
    removeListener(auctionStore.leaderChangedListener);
  }
  initLeaderChangedListener(activeLot);

  // Leaders list (for seller)
  if (auctionStore.leadersListUpdatedListener) {
    removeListener(auctionStore.leadersListUpdatedListener);
  }
  initLeadersListUpdatedListener(activeLot);

  // Bids count
  if (auctionStore.bidsCountListener) {
    removeListener(auctionStore.bidsCountListener);
  }
  initBidsCountListener(activeLot);

  // Book bid
  if (auctionStore.bookBidListener) {
    removeListener(auctionStore.bookBidListener);
  }
  initBookBidListener(activeLot);
}

export function removeListener(channel: string) {
  window.Echo.leaveChannel(channel);
  window.Echo.leave(channel);
}
